import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Loader2, Download } from "lucide-react";
import { PublicShell } from "@/components/PublicShell";

const META = {
  privacy: { title: "Privacy Policy", subtitle: "How Treasurescare collects, uses and safeguards your personal data.", file: "/legal/privacy.md", pdf: "/legal/privacy.pdf" },
  terms:   { title: "Terms and Conditions", subtitle: "The agreement between you and Treasurescare when you use our service.", file: "/legal/terms.md", pdf: "/legal/terms.pdf" },
};

// Minimal markdown renderer — headings, paragraphs, lists, tables, bold, italic, code, links
function renderMarkdown(md) {
  const lines = md.split("\n");
  const blocks = [];
  let paragraph = [];
  let list = null;
  let table = null;
  const flushPara = () => { if (paragraph.length) { blocks.push({ kind: "p", text: paragraph.join(" ") }); paragraph = []; } };
  const flushList = () => { if (list) { blocks.push({ kind: "ul", items: list }); list = null; } };
  const flushTable = () => { if (table) { blocks.push({ kind: "table", ...table }); table = null; } };
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) { flushPara(); flushList(); flushTable(); continue; }
    if (line.startsWith("# ")) { flushPara(); flushList(); flushTable(); blocks.push({ kind: "h1", text: line.slice(2) }); continue; }
    if (line.startsWith("## ")) { flushPara(); flushList(); flushTable(); blocks.push({ kind: "h2", text: line.slice(3) }); continue; }
    if (line.startsWith("### ")) { flushPara(); flushList(); flushTable(); blocks.push({ kind: "h3", text: line.slice(4) }); continue; }
    if (line.startsWith("---")) { flushPara(); flushList(); flushTable(); blocks.push({ kind: "hr" }); continue; }
    if (line.startsWith("- ") || line.startsWith("* ")) { flushPara(); flushTable(); if (!list) list = []; list.push(line.slice(2)); continue; }
    if (line.startsWith("|") && line.endsWith("|")) {
      flushPara(); flushList();
      const cells = line.slice(1, -1).split("|").map(c => c.trim());
      if (cells.every(c => /^:?-+:?$/.test(c))) continue;
      if (!table) table = { header: cells, rows: [] };
      else table.rows.push(cells);
      continue;
    }
    flushList(); flushTable();
    paragraph.push(line);
  }
  flushPara(); flushList(); flushTable();
  return blocks;
}

function inline(text) {
  const patterns = [
    // Compound patterns first — bold-wrapping-link, then link-wrapping-bold
    { re: /\*\*\[(.+?)\]\((.+?)\)\*\*/, wrap: (m, k) => <a key={k} href={m[2]} className="text-primary hover:underline font-semibold">{m[1]}</a> },
    { re: /\[\*\*(.+?)\*\*\]\((.+?)\)/, wrap: (m, k) => <a key={k} href={m[2]} className="text-primary hover:underline font-semibold">{m[1]}</a> },
    { re: /\[(.+?)\]\((.+?)\)/, wrap: (m, k) => <a key={k} href={m[2]} className="text-primary hover:underline">{m[1]}</a> },
    { re: /\*\*(.+?)\*\*/, wrap: (m, k) => <b key={k}>{m[1]}</b> },
    { re: /`([^`]+)`/, wrap: (m, k) => <code key={k} className="text-xs bg-muted px-1 rounded">{m[1]}</code> },
    { re: /\*(.+?)\*/, wrap: (m, k) => <i key={k}>{m[1]}</i> },
  ];
  let out = [text];
  let key = 0;
  for (const { re, wrap } of patterns) {
    const next = [];
    for (const chunk of out) {
      if (typeof chunk !== "string") { next.push(chunk); continue; }
      let s = chunk, m;
      while ((m = s.match(re))) {
        if (m.index > 0) next.push(s.slice(0, m.index));
        next.push(wrap(m, key++));
        s = s.slice(m.index + m[0].length);
      }
      if (s) next.push(s);
    }
    out = next;
  }
  return out;
}

export default function LegalPage({ slug }) {
  const meta = META[slug];
  const [md, setMd] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!meta) return;
    fetch(meta.file).then(r => r.ok ? r.text() : Promise.reject(r.status)).then(setMd).catch(setErr);
  }, [meta]);
  if (!meta) return <PublicShell><div className="p-20 text-center">Not found.</div></PublicShell>;
  const blocks = md ? renderMarkdown(md) : [];
  return (
    <PublicShell>
      <div className="max-w-3xl mx-auto px-4 lg:px-8 py-10 lg:py-16">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1">
            <h1 className="font-display text-4xl font-light tracking-tight" data-testid="legal-title">{meta.title}</h1>
            <p className="text-sm text-muted-foreground mt-1">{meta.subtitle}</p>
          </div>
          <a
            href={meta.pdf}
            download
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border hover:border-primary/50 hover:bg-primary/5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid={`legal-${slug}-pdf-btn`}
          >
            <Download className="w-3.5 h-3.5" /> PDF
          </a>
        </div>
        {err && <div className="p-4 rounded bg-rose-500/10 text-rose-500 text-sm">Failed to load document ({String(err)}).</div>}
        {!md && !err && <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>}
        {md && (
          <article className="prose prose-sm max-w-none text-foreground space-y-3" data-testid={`legal-${slug}-page`}>
            {blocks.map((b, i) => {
              if (b.kind === "h1") return <h1 key={i} className="font-display text-3xl mt-8 mb-2">{inline(b.text)}</h1>;
              if (b.kind === "h2") return <h2 key={i} className="font-display text-2xl mt-6 mb-2">{inline(b.text)}</h2>;
              if (b.kind === "h3") return <h3 key={i} className="font-display text-lg mt-4 mb-1 text-primary">{inline(b.text)}</h3>;
              if (b.kind === "hr") return <hr key={i} className="my-6 border-border" />;
              if (b.kind === "ul") return <ul key={i} className="list-disc pl-6 space-y-1">{b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}</ul>;
              if (b.kind === "table") return (
                <div key={i} className="overflow-x-auto my-3">
                  <table className="text-sm border border-border">
                    <thead className="bg-muted"><tr>{b.header.map((h, j) => <th key={j} className="p-2 text-left font-medium border-b border-border">{inline(h)}</th>)}</tr></thead>
                    <tbody>{b.rows.map((r, j) => <tr key={j} className="border-b border-border/50">{r.map((c, k) => <td key={k} className="p-2 align-top">{inline(c)}</td>)}</tr>)}</tbody>
                  </table>
                </div>
              );
              return <p key={i} className="leading-relaxed">{inline(b.text)}</p>;
            })}
          </article>
        )}
        <div className="mt-10 text-xs text-muted-foreground">
          <Link to="/" className="hover:text-foreground">← Back to home</Link>
          {" · "}
          {slug === "privacy" ? <Link to="/terms" className="hover:text-foreground">Terms &amp; conditions</Link> : <Link to="/privacy" className="hover:text-foreground">Privacy policy</Link>}
        </div>
      </div>
    </PublicShell>
  );
}
